import React, { useEffect, useMemo } from 'react';
import { HashRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import MainLayout from './components/Layout/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/home';
import Conversations from './pages/conversations';
import Analytics from './pages/analytics';
import Login from './pages/login';
import Training from './pages/Training';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';
import Implementation from './pages/Implementation';
import Partners from './pages/Partners';
import Usuarios from './pages/Usuarios';
import Cuenta from './pages/Cuenta';
import Support from './pages/Support';
import Contact from './pages/Contact';
import RecuperarPassword from './pages/recuperar-password';
import ResetPassword from './pages/reset-password';
import NewSubscription from './pages/new-subscription';
import ScrapingPrompt from './pages/ScrapingPrompt';
import AdminOptions from './pages/AdminOptions';
import { DateProvider } from './context/DateContext';
import { SelectedChatbotProvider } from './context/SelectedChatbotContext';
import { UserProvider } from './context/UserContext';
import { ThemeModeProvider, useThemeMode } from './context/ThemeContext';
import './App.css';
import ReactGA from "react-ga4";

const TRACKING_ID = "G-ZNB48NVGPP"; // Reemplázalo con tu ID de Google Analytics

const sharedTypography = {
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  allVariants: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  body1: { fontSize: '1rem', lineHeight: 1.65, fontWeight: 500 },
  body2: { fontSize: '0.92rem', lineHeight: 1.6, fontWeight: 500 },
  subtitle1: { fontSize: '1rem', lineHeight: 1.5, fontWeight: 600 },
  subtitle2: { fontSize: '0.9rem', lineHeight: 1.45, fontWeight: 600 },
  h1: { fontWeight: 800, letterSpacing: '-0.025em' },
  h2: { fontWeight: 800, letterSpacing: '-0.02em' },
  h3: { fontWeight: 700, letterSpacing: '-0.01em' },
  h4: { fontWeight: 700, letterSpacing: '-0.01em', fontSize: '2rem' },
  h5: { fontWeight: 700, fontSize: '1.35rem' },
  h6: { fontWeight: 700, fontSize: '1.08rem' },
  button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
};

const sharedShape = { borderRadius: 16 };

const sharedButtonOverrides = {
  root: {
    borderRadius: 12,
    padding: '11px 24px',
    minHeight: 44,
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'none',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': { transform: 'scale(1.01)' },
    '&:active': { transform: 'scale(0.98)' },
  },
  containedPrimary: {
    background: 'linear-gradient(180deg, #f97316 0%, #ea580c 100%)',
    boxShadow: '0 4px 12px rgba(249, 115, 22, 0.2)',
    '&:hover': {
      background: 'linear-gradient(180deg, #ea580c 0%, #c2410c 100%)',
      boxShadow: '0 6px 16px rgba(249, 115, 22, 0.3)',
    },
  },
};

const sharedChipOverrides = {
  root: {
    borderRadius: 10,
    fontWeight: 700,
    height: 28,
    fontSize: '0.78rem'
  }
};

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#f97316', light: '#fb923c', dark: '#ea580c', contrastText: '#ffffff' },
    secondary: { main: '#0f172a', light: '#475569', dark: '#020617', contrastText: '#ffffff' },
    background: { default: '#f1f3f6', paper: '#f7f8fa' },
    text: { primary: '#0f172a', secondary: '#475569' },
    divider: 'rgba(0,0,0,0.08)',
    action: { hover: 'rgba(249, 115, 22, 0.04)', selected: 'rgba(249, 115, 22, 0.08)' },
    success: { main: '#22c55e' },
    error: { main: '#ef4444' },
    warning: { main: '#f59e0b' },
    info: { main: '#3b82f6' },
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#f1f3f6',
          backgroundImage: `
            radial-gradient(ellipse 80% 50% at 50% -20%, rgba(249, 115, 22, 0.06) 0%, transparent 60%),
            linear-gradient(rgba(0, 0, 0, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 0, 0, 0.04) 1px, transparent 1px)
          `,
          backgroundSize: '100% 100%, 48px 48px, 48px 48px',
          backgroundAttachment: 'fixed',
        },
      },
    },
    MuiButton: { styleOverrides: sharedButtonOverrides },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#f7f8fa',
          borderRadius: 20,
          border: '1px solid rgba(0, 0, 0, 0.07)',
          boxShadow: '0 2px 6px rgba(15,23,42,0.05), 0 12px 32px rgba(15,23,42,0.05)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(247, 248, 250, 0.9)',
          backdropFilter: 'blur(30px)',
          WebkitBackdropFilter: 'blur(30px)',
          borderRight: '1px solid rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(241, 243, 246, 0.75)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          boxShadow: 'none',
          color: '#0f172a',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            fontSize: '0.98rem',
            fontWeight: 500,
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.96rem',
            fontWeight: 500,
          },
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
            borderRadius: 12,
            minHeight: 48,
            '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.08)' },
            '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.15)' },
            '&.Mui-focused fieldset': { borderColor: '#f97316', borderWidth: 1.5 },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: '1px solid rgba(0, 0, 0, 0.04)', padding: '20px 18px', color: '#475569', fontSize: '0.97rem', fontWeight: 500 },
        head: {
          fontWeight: 700, color: '#94a3b8', fontSize: '0.78rem',
          textTransform: 'uppercase', letterSpacing: '0.05em', backgroundColor: '#edf0f4',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.2s ease',
        },
      },
    },
    MuiChip: { styleOverrides: sharedChipOverrides },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontSize: '0.98rem',
          fontWeight: 500,
          minHeight: 48,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.08)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.15)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f97316' },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#f7f8fa',
          border: '1px solid rgba(0, 0, 0, 0.07)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 46,
          borderRadius: 12,
          paddingTop: 10,
          paddingBottom: 10,
          paddingLeft: 12,
          paddingRight: 12,
          '& .MuiListItemText-primary': { fontSize: '0.98rem', fontWeight: 600 },
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.03)' },
          '&.Mui-selected': { backgroundColor: 'rgba(249, 115, 22, 0.08)', color: '#f97316' },
          '&.Mui-selected:hover': { backgroundColor: 'rgba(249, 115, 22, 0.12)' },
        },
      },
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#f97316', light: '#fb923c', dark: '#ea580c', contrastText: '#ffffff' },
    secondary: { main: '#e2e8f0', light: '#f1f5f9', dark: '#cbd5e1', contrastText: '#0f172a' },
    background: { default: '#0c1222', paper: '#1a2332' },
    text: { primary: '#e2e8f0', secondary: '#94a3b8' },
    divider: 'rgba(255,255,255,0.08)',
    action: { hover: 'rgba(249, 115, 22, 0.08)', selected: 'rgba(249, 115, 22, 0.12)' },
    success: { main: '#22c55e' },
    error: { main: '#ef4444' },
    warning: { main: '#f59e0b' },
    info: { main: '#3b82f6' },
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0c1222',
          backgroundImage: `
            radial-gradient(ellipse 80% 50% at 50% -20%, rgba(249, 115, 22, 0.12) 0%, transparent 60%),
            linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px)
          `,
          backgroundSize: '100% 100%, 48px 48px, 48px 48px',
          backgroundAttachment: 'fixed',
        },
      },
    },
    MuiButton: { styleOverrides: sharedButtonOverrides },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#1a2332',
          borderRadius: 20,
          border: '1px solid rgba(255, 255, 255, 0.06)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.22), 0 14px 36px rgba(0,0,0,0.18)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          backdropFilter: 'blur(30px)',
          WebkitBackdropFilter: 'blur(30px)',
          borderRight: '1px solid rgba(255, 255, 255, 0.06)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          boxShadow: 'none',
          color: '#e2e8f0',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            fontSize: '0.98rem',
            fontWeight: 500,
          },
          '& .MuiInputLabel-root': { color: '#94a3b8', fontSize: '0.96rem', fontWeight: 500 },
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            borderRadius: 12,
            minHeight: 48,
            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
            '&.Mui-focused fieldset': { borderColor: '#f97316', borderWidth: 1.5 },
          },
          '& .MuiInputBase-input': { color: '#e2e8f0' },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: '1px solid rgba(255, 255, 255, 0.06)', padding: '20px 18px', color: '#94a3b8', fontSize: '0.97rem', fontWeight: 500 },
        head: {
          fontWeight: 700, color: '#64748b', fontSize: '0.78rem',
          textTransform: 'uppercase', letterSpacing: '0.05em', backgroundColor: '#111b2b',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.2s ease',
        },
      },
    },
    MuiChip: { styleOverrides: sharedChipOverrides },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontSize: '0.98rem',
          fontWeight: 500,
          minHeight: 48,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.1)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f97316' },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1a2332',
          border: '1px solid rgba(255, 255, 255, 0.06)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 46,
          borderRadius: 12,
          paddingTop: 10,
          paddingBottom: 10,
          paddingLeft: 12,
          paddingRight: 12,
          '& .MuiListItemText-primary': { fontSize: '0.98rem', fontWeight: 600 },
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
          '&.Mui-selected': { backgroundColor: 'rgba(249, 115, 22, 0.08)', color: '#f97316' },
          '&.Mui-selected:hover': { backgroundColor: 'rgba(249, 115, 22, 0.12)' },
        },
      },
    },
  },
});

const RedirectComponent = () => {
  const navigate = useNavigate();
  useEffect(() => {
    if (window.location.hash === '') {
      const user = JSON.parse(sessionStorage.getItem('user'));
      navigate(user ? '/' : '/login');
    }
  }, [navigate]);
  return null;
};

const ThemedApp = () => {
  const { mode } = useThemeMode();
  const theme = useMemo(() => (mode === 'light' ? lightTheme : darkTheme), [mode]);

  useEffect(() => {
    ReactGA.initialize(TRACKING_ID);
    ReactGA.send("pageview");
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <UserProvider>
        <SelectedChatbotProvider>
          <DateProvider>
            <Router>
              <RedirectComponent />
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/recuperar-password" element={<RecuperarPassword />} />
                <Route path="/pass-recover" element={<ResetPassword />} />
                <Route path="/new_subscription" element={<NewSubscription />} />
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <Routes>
                          <Route path="/" element={<Home />} />
                          <Route path="/conversations" element={<Conversations />} />
                          <Route path="/analytics" element={<Analytics />} />
                          <Route path="/training" element={<Training />} />
                          <Route path="/notifications" element={<Notifications />} />
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/implementation" element={<Implementation />} />
                          <Route path="/partners" element={<Partners />} />
                          <Route path="/users" element={<Usuarios />} />
                          <Route path="/account" element={<Cuenta />} />
                          <Route path="/scraping-prompt" element={<ScrapingPrompt />} />
                          <Route path="/admin-options" element={<AdminOptions />} />
                          <Route path="/support" element={<Support />} />
                          <Route path="/contact" element={<Contact />} />
                        </Routes>
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Router>
          </DateProvider>
        </SelectedChatbotProvider>
      </UserProvider>
    </ThemeProvider>
  );
};

function App() {
  return (
    <ThemeModeProvider>
      <ThemedApp />
    </ThemeModeProvider>
  );
}

export default App;
